/**
 * <s-scroll-reveal> — Fades and lifts page regions in as they scroll into view.
 *
 * Targets every direct child region of this element plus any descendant marked
 * [data-reveal-target], and anything in the document marked .reveal-on-scroll
 * (the footer). Regions already on screen when the page loads are left alone so
 * the first view never flashes or waits. Off-screen regions get [data-reveal]
 * (hidden by CSS) and receive [data-revealed] once they enter the viewport.
 * Visitors who prefer reduced motion, and browsers without
 * IntersectionObserver, see everything immediately.
 */
class SScrollReveal extends HTMLElement {
  #observer;

  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = new Set([
      ...this.querySelectorAll(':scope > *, [data-reveal-target]'),
      ...document.querySelectorAll('.reveal-on-scroll'),
    ]);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      rootMargin: '0px 0px -18% 0px',
      threshold: 0.12,
    });

    // Batch all layout reads before any attribute writes so the browser lays
    // out the page once instead of once per target (avoids forced reflows).
    const fold = window.innerHeight;
    const belowFold = [];
    for (const target of targets) {
      if (target.matches('script, style, template, link, [data-no-reveal]')) continue;
      if (target.getBoundingClientRect().top < fold) continue;
      belowFold.push(target);
    }
    for (const target of belowFold) {
      target.setAttribute('data-reveal', '');
      this.#observer.observe(target);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.setAttribute('data-revealed', '');
      this.#observer.unobserve(entry.target);
    }
  };
}

if (!customElements.get('s-scroll-reveal')) {
  customElements.define('s-scroll-reveal', SScrollReveal);
}
